import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicHeader } from "../components/cod/PublicHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ChicagoStars, CODLogoSVG } from "../components/cod/Logo";
import { marketApi } from "../lib/api";
import { ArrowRight, Route, CheckCircle2, Users, Compass, ShieldCheck } from "lucide-react";
import { Seo } from "../components/cod/Seo";

export default function Pathways() {
  const [bundles, setBundles] = useState([]);
  const [services, setServices] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    Promise.all([marketApi.bundles(), marketApi.opportunities()]).then(([b, s]) => { setBundles(b); setServices(s); });
  }, []);
  const serviceById = Object.fromEntries(services.map(s => [s.id, s]));

  return (
    <div className="min-h-screen bg-white" data-testid="pathways-page">
      <Seo
        title="Career Pathways"
        description="Healthcare, business, and workforce pathways offered by Chicago Opportunity Desk — CNA → PCT, business formation → government contracting, and more, with credentials, prerequisites, and next steps."
        path="/pathways"
        schema="ProfessionalService"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Pathways", path: "/pathways" }]}
        faq={[
          { q: "How long does a pathway take?", a: "It depends on the credential. Food Handler is same-week. CNA typically runs 6–8 weeks. Government Contract Accelerator prep is 8–12 weeks. Your Case Manager will confirm a realistic timeline based on prerequisites already in place." },
          { q: "Do I have to pay for the whole pathway upfront?", a: "No. Many services are free for qualified participants; others are funded through workforce partners. Your Case Manager confirms cost tier before you commit to any step." },
          { q: "What if I fail a background check?", a: "COD helps you understand what surfaced and whether pathways like Home Care Aide, business services, or Food Handler credentials are still viable. Many participants continue with a different pathway when clinical roles are blocked." },
          { q: "Can I switch pathways later?", a: "Yes. Your Case Manager can rebuild your Opportunity Plan as circumstances change. Credentials you've already earned come with you." },
          { q: "Do employers know about COD pathways?", a: "Yes. COD works directly with Chicago employers who recognize the pathway credentials and often reserve interview slots for COD participants." },
          { q: "I'm not sure which pathway fits.", a: "Start with the Opportunity Navigator. A single conversation is enough to identify the right pathway family and a realistic first step." },
        ]}
      />
      <PublicHeader/>

      {/* Hero */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="cod-eyebrow">Pathways</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-3">A sequenced way forward.</h1>
          <div className="cod-rule-red mt-5"/>
          <p className="mt-5 text-lg text-slate-700 max-w-3xl">A COD Pathway is a curated set of services placed in the right order — so a Chicago resident does not have to guess whether they should get a credential first, complete a background check, take an interview prep session, or start a business filing. Every pathway is supported by a real COD Case Manager, and each step feeds directly into the next.</p>
        </div>
      </section>

      {/* Why pathways */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {icon: Route, t: "Sequenced by design", d: "Each pathway groups services in the exact order they should be completed. No wasted certifications, no dead-end coursework."},
            {icon: Users, t: "Case-manager supported", d: "You are not walking a pathway alone. A COD Case Manager is assigned early and stays with you through the last step."},
            {icon: ShieldCheck, t: "Barrier-aware", d: "COD identifies fingerprinting, background checks, transportation, childcare, or funding barriers up front — not after you've already invested time."},
          ].map(b => (
            <Card key={b.t} className="cod-card-elev">
              <CardContent className="pt-6">
                <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3" style={{background:"var(--cod-navy-soft)"}}>
                  <b.icon size={20} style={{color:"var(--cod-navy)"}}/>
                </div>
                <h3 className="font-serif text-xl text-slate-900">{b.t}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{b.d}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Pathway families */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <p className="cod-eyebrow mb-2">Pathway families</p>
        <h2 className="font-serif text-3xl text-slate-900">Three tracks. One coordinated experience.</h2>
        <div className="cod-rule-red mt-4 mb-6"/>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="cod-card-elev" data-testid="pathway-family-healthcare">
            <CardContent className="pt-6">
              <p className="cod-eyebrow mb-1">Healthcare</p>
              <h3 className="font-serif text-2xl text-slate-900">Direct-care and clinical support careers</h3>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">CNA → PCT progression, Phlebotomy, EKG, Medical Coding &amp; Billing, and Home Care Aide. Fingerprinting and background clearance are built into the pathway, not bolted on at the last minute.</p>
              <p className="text-xs uppercase tracking-widest text-slate-500 mt-4 font-semibold">Example flow</p>
              <p className="text-sm text-slate-700 mt-1">Career Navigation → CPR/BLS → Fingerprinting → Background Check → CNA → PCT</p>
            </CardContent>
          </Card>
          <Card className="cod-card-elev" data-testid="pathway-family-business">
            <CardContent className="pt-6">
              <p className="cod-eyebrow mb-1">Business &amp; contracting</p>
              <h3 className="font-serif text-2xl text-slate-900">From formation to government contracts</h3>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">Business Formation → Capability Statements → Vendor Registration → Government Contract Accelerator → Procurement Readiness. Structured for Chicago entrepreneurs pursuing MBE/WBE/DBE work.</p>
              <p className="text-xs uppercase tracking-widest text-slate-500 mt-4 font-semibold">Example flow</p>
              <p className="text-sm text-slate-700 mt-1">Business Formation → Capability Statement → Vendor Registration → Government Contract Accelerator</p>
            </CardContent>
          </Card>
          <Card className="cod-card-elev" data-testid="pathway-family-workforce">
            <CardContent className="pt-6">
              <p className="cod-eyebrow mb-1">Workforce</p>
              <h3 className="font-serif text-2xl text-slate-900">Job-ready credentials and placement</h3>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">Workforce Readiness, Career Navigation, Resume Assistance, Interview Preparation, Food Handler, Food Manager, ServSafe Alcohol, and Job Placement. Includes optional Security PERC and CPR/BLS tracks.</p>
              <p className="text-xs uppercase tracking-widest text-slate-500 mt-4 font-semibold">Example flow</p>
              <p className="text-sm text-slate-700 mt-1">Workforce Readiness → Resume → Interview Prep → Credential (Food Handler / CPR) → Job Placement</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Live bundles from marketplace */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="cod-eyebrow mb-2">Curated bundles</p>
            <h2 className="font-serif text-3xl text-slate-900">Ready-to-start pathways</h2>
            <div className="cod-rule-red mt-3"/>
          </div>
          <Link to="/marketplace" className="hidden sm:inline-flex text-sm font-semibold cod-link text-[var(--cod-navy)]" data-testid="pathways-browse-marketplace">Browse every service <ArrowRight size={14} className="ml-1"/></Link>
        </div>
        <div className="space-y-6">
          {bundles.map(b => (
            <Card key={b.id} className="cod-card-elev" data-testid={`pathway-${b.id}`}>
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{background:"var(--cod-navy-soft)"}}>
                    <Route size={18} style={{color:"var(--cod-navy)"}}/>
                  </div>
                  <div className="flex-1">
                    <CardTitle className="font-serif text-2xl">{b.name}</CardTitle>
                    <p className="text-sm text-slate-600 mt-1">{b.description}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-3 font-semibold">Services in this pathway</div>
                <div className="flex flex-wrap gap-2">
                  {b.opportunities.map(oid => serviceById[oid]).filter(Boolean).map(s => (
                    <button key={s.id} onClick={()=>navigate(`/marketplace/${s.id}`)}
                      className="px-3 py-1.5 rounded-md text-sm border border-slate-200 hover:border-[var(--cod-navy)] hover:bg-[var(--cod-navy-soft)] transition-colors">
                      {s.name}
                    </button>
                  ))}
                </div>
                <Button onClick={()=>navigate("/start")} className="mt-5 bg-[var(--cod-red)] hover:bg-[#A40D26] text-white">
                  Start this pathway <ArrowRight size={14} className="ml-2"/>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* How a pathway feels */}
      <section className="cod-section" style={{background:"var(--cod-navy-soft)"}}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="cod-eyebrow mb-2">How a pathway feels</p>
          <h2 className="font-serif text-3xl text-slate-900">Four things every COD pathway includes</h2>
          <div className="cod-rule-red mt-4 mb-6"/>
          <ul className="grid md:grid-cols-2 gap-4 max-w-4xl">
            {[
              "A named COD Case Manager assigned before your second step.",
              "A written Opportunity Plan you can revisit any time.",
              "Every prerequisite (background check, ID, fingerprinting) surfaced early.",
              "A confirmed employer, program, or referral at the end — not just a certificate.",
            ].map(item => (
              <li key={item} className="flex gap-3 text-slate-800"><CheckCircle2 size={20} style={{color:"var(--cod-red)"}} className="mt-0.5 flex-shrink-0"/> {item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="pathways-faq">
        <p className="cod-eyebrow mb-2">Common questions</p>
        <h2 className="font-serif text-3xl text-slate-900">Pathway FAQ</h2>
        <div className="cod-rule-red mt-4 mb-8"/>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            {q:"How long does a pathway take?", a:"It depends on the credential. Food Handler is same-week. CNA typically runs 6–8 weeks. Government Contract Accelerator prep is 8–12 weeks. Your Case Manager will confirm a realistic timeline based on prerequisites already in place."},
            {q:"Do I have to pay for the whole pathway upfront?", a:"No. Many services are free for qualified participants; others are funded through workforce partners. Your Case Manager confirms cost tier before you commit to any step."},
            {q:"What if I fail a background check?", a:"COD helps you understand what surfaced and whether pathways like Home Care Aide, business services, or Food Handler credentials are still viable. Many participants continue with a different pathway when clinical roles are blocked."},
            {q:"Can I switch pathways later?", a:"Yes. Your Case Manager can rebuild your Opportunity Plan as circumstances change. Credentials you've already earned come with you."},
            {q:"Do employers know about COD pathways?", a:"Yes. COD works directly with Chicago employers who recognize the pathway credentials and often reserve interview slots for COD participants."},
            {q:"I'm not sure which pathway fits.", a:"Start with the Opportunity Navigator. A single conversation is enough to identify the right pathway family and a realistic first step."},
          ].map(item => (
            <div key={item.q} className="p-5 rounded-md border border-slate-200 bg-white">
              <h3 className="font-serif text-lg text-slate-900">{item.q}</h3>
              <p className="text-sm text-slate-700 mt-2 leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Compass size={28} className="mx-auto mb-3" style={{color:"var(--cod-navy)"}}/>
          <h2 className="font-serif text-3xl text-slate-900">Not sure where to begin?</h2>
          <p className="text-slate-700 mt-4">Start a Navigator conversation. In under ten minutes you'll have a suggested pathway, an honest cost picture, and a first concrete step.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/start"><Button size="lg" className="bg-[var(--cod-red)] hover:bg-[#A40D26] text-white px-8 py-7" data-testid="pathways-cta-start">Talk to the Navigator</Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="px-8 py-7" data-testid="pathways-cta-contact">Contact a Case Manager</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
